import { __decorate, __metadata } from "tslib";
import { beforeProperties } from '@dojo/framework/core/decorators/beforeProperties';
import { theme } from '@dojo/framework/core/mixins/Themed';
import { v, w } from '@dojo/framework/core/vdom';
import { I18nThemed } from '@holmescorp/interfaces/mixins';
import { lccstore, userstore } from '@holmescorp/lcc-state';
import { logQuiz } from '@holmescorp/logger';
import { mayTriggerML } from '@holmescorp/ml';
import { publish } from '@holmescorp/pub-sub';
import css from '../css/questionfeedback.m.css';
import { svc as auditsvc } from '../support/audit-svc';
import { svc } from '../support/qfsvc';
import { QuestionList } from '../support/questionlist';
import quizsvc from '../support/quizsvc';
import { infoChange, quizChange, setInreview, setIsaudit, setProgressionIsrunning, setQuestion, setTitle } from '../support/state';
import { recordProgress, recordStart } from '../support/xapi';
let BaseProgression = class BaseProgression extends I18nThemed {
    startquiz = { cursor: 0, status: 'question' };
    svc = svc;
    auditsvc = auditsvc;
    _propsInited = false;
    onAttach() {
        const { stopLoading, quizstore } = this.properties;
        stopLoading && stopLoading();
        setProgressionIsrunning(quizstore)({ isrunning: '' });
    }
    onDetach() {
    }
    async load_quiz() {
        const { quizurl = '', quizstore } = this.properties;
        const { svc } = this;
        svc._smd.url = quizurl;
        const [quiz, info] = await Promise.all([svc.quiz(), svc.info()]);
        const { title } = quiz;
        setTitle(quizstore)({ title });
        quizChange(quizstore)({ quiz });
        infoChange(quizstore)({ info });
        setQuestion(quizstore)({ question: undefined });
        return true;
    }
    async quiz_startup(isRestart = false) {
        const { svc } = this;
        const { quizstore } = this.properties;
        const quiz = await svc.quiz();
        const info = await svc.info();
        setProgressionIsrunning(quizstore)({ isrunning: 'quiz' });
        quizChange(quizstore)({ quiz });
        infoChange(quizstore)({ info });
        setQuestion(quizstore)({ question: undefined });
        info.runningtime && !isRestart ? recordProgress(quiz, info) : recordStart(quiz, isRestart);
        this.invalidate();
        publish('quiz:status-change', { quizid: quiz.id });
        return true;
    }
    async finalize() {
        const { quizstore } = this.properties;
        const { get, path } = userstore;
        const { svc } = this;
        const userid = get(path('user_id'));
        const { value: studyplan } = path('toolstate', 'studyplan');
        const [quiz, info] = await Promise.all([svc.quiz(), svc.info()]);
        setProgressionIsrunning(quizstore)({ isrunning: 'final' });
        quizChange(quizstore)({ quiz });
        infoChange(quizstore)({ info });
        setQuestion(quizstore)({ question: undefined });
        await quizsvc.update_annotation({ userid });
        this.invalidate();
        if (quiz.kind === 'pretest' && !studyplan.ml.inFlight) {
            mayTriggerML(true);
        }
        publish('quiz:status-change', { quizid: quiz.id, status: 'final' });
        return true;
    }
    render_loading() {
        const { key } = this.properties;
        return [
            v('div', { key: `spinner-${key}`, classes: [this.theme(css.loading)] }, [
                v('i', { classes: ['fa', 'fa-spinner', 'fa-spin', 'fa-lg'] })
            ])
        ];
    }
    audit_init() {
        const { quizurl = '' } = this.properties;
        const { auditsvc } = this;
        logQuiz.info(`run_audit quizurl = "${quizurl}"`);
        auditsvc._smd.url = quizurl;
    }
    audit_startup() {
        const { localizedBundle: { messages: { audit: auditText = '' } }, quiz, quizstore } = this.properties;
        setProgressionIsrunning(quizstore)({ isrunning: 'audit' });
        setTitle(quizstore)({ title: `${quiz ? quiz.title : ''} ${auditText}` });
        setQuestion(quizstore)({ question: undefined });
        this.invalidate();
        return Promise.resolve(true);
    }
    run_audit() {
        const { quiz = {}, info, quizstore } = this.properties;
        const { startquiz, auditsvc } = this;
        const resultid = info && info.lastresult.id;
        const finalize = () => {
            setIsaudit(quizstore)({ isaudit: false });
            setInreview(quizstore)({ inreview: false });
            setTitle(quizstore)({ title: quiz.title });
            setProgressionIsrunning(quizstore)({ isrunning: '' });
            this.invalidate();
        };
        return [w(QuestionList, { svc: auditsvc, finalize, startquiz, resultid, quizstore })];
    }
    ensure_nls(properties) {
        const { get, path } = lccstore;
        const dynls = get(path('dynls'));
        const bundle = dynls && dynls.get('quiz');
        properties.localizedBundle = bundle
            ? this.localizeBundle(bundle)
            : { isPlaceholder: true, format: null, messages: {} };
        properties.quizid = properties.quiz ? properties.quiz.id : '';
        return properties;
    }
    propsInit(paths, store) {
        if (!this._propsInited) {
            const watch = (store, paths) => {
                const invalidatePaths = paths.map((path) => store.path(path.join('/')));
                const handle = store.onChange(invalidatePaths, () => this.invalidate());
                this.own({
                    destroy: () => {
                        handle.remove();
                    }
                });
            };
            watch(store, paths);
            watch(lccstore, [['dynls']]);
            this._propsInited = true;
        }
    }
    stateProperties(properties) {
        const { quizstore } = properties;
        const { get, path } = quizstore;
        this.propsInit([['quiz'], ['info'], ['quizurl'], ['progressionState', 'isrunning'], ['instancekey']], quizstore);
        properties.quiz = get(path('quiz'));
        properties.info = get(path('info'));
        properties.quizurl = get(path('quizurl'));
        properties.isrunning = get(path('progressionState', 'isrunning'));
        properties.stopLoading = get(path('stopLoading'));
        properties.instancekey = get(path('instancekey'));
        properties.runningmpe = userstore.get(userstore.path('mpe'));
        properties.activempe = get(path('activeSection'));
        return properties;
    }
};
__decorate([
    beforeProperties(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", Object)
], BaseProgression.prototype, "ensure_nls", null);
__decorate([
    beforeProperties(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object]),
    __metadata("design:returntype", Object)
], BaseProgression.prototype, "stateProperties", null);
BaseProgression = __decorate([
    theme(css)
], BaseProgression);
export { BaseProgression };
